import { Navigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useFiscalStore } from "@/store/fiscal-store";
import { fmtBRL, fmtNum, fmtDur, fmtDate } from "@/lib/format";
import { exportDashboardPdf } from "@/lib/export-pdf";
import { Download, FileText, Table2, Clock, CheckCircle2, Loader2, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import type { NotaStatus } from "@/lib/fiscal-types";

interface ExportLog {
  id: string;
  tipo: "PDF" | "Excel";
  timestamp: string;
  totalNotas: number;
  status: "success" | "error";
  label: string;
}

export default function ExportacoesPage() {
  const { dashboard, notas, divergencias, duracaoMs, setFiltroStatus } = useFiscalStore();
  const [exportando, setExportando] = useState<"pdf" | "excel" | null>(null);
  const [logs, setLogs] = useState<ExportLog[]>([]);
  const [filtroStatus, setFiltroStatusLocal] = useState<NotaStatus | "all">("all");
  const [filtroModelo, setFiltroModelo] = useState<"all" | "55" | "65">("all");
  const tickRef = useRef<number | null>(null);

  useEffect(() => { document.title = "Exportações — Extrator Fiscal"; }, []);
  useEffect(() => () => { if (tickRef.current) clearInterval(tickRef.current); }, []);

  if (!dashboard) return <Navigate to="/upload" replace />;

  const notasFiltradas = notas.filter((n) => {
    if (filtroStatus !== "all" && n.notaStatus !== filtroStatus) return false;
    if (filtroModelo !== "all" && String(n.modelo) !== filtroModelo) return false;
    return true;
  });

  const addLog = (log: ExportLog) => setLogs((prev) => [log, ...prev].slice(0, 20));

  const handlePDF = () => {
    if (exportando) return;
    setExportando("pdf");
    setTimeout(() => {
      try {
        exportDashboardPdf(dashboard, notasFiltradas, duracaoMs);
        toast.success("PDF exportado com sucesso.");
        addLog({ id: crypto.randomUUID(), tipo: "PDF", timestamp: new Date().toISOString(), totalNotas: notasFiltradas.length, status: "success", label: `Dashboard + ${fmtNum(notasFiltradas.length)} notas` });
      } catch (e: any) {
        toast.error("Falha ao gerar PDF: " + (e?.message ?? "erro desconhecido"));
        addLog({ id: crypto.randomUUID(), tipo: "PDF", timestamp: new Date().toISOString(), totalNotas: notasFiltradas.length, status: "error", label: "Erro na exportação" });
      } finally {
        setExportando(null);
      }
    }, 50);
  };

  const handleExcel = () => {
    if (exportando) return;
    setExportando("excel");
    setTimeout(() => {
      try {
        const headers = ["Chave", "Modelo", "Número", "Série", "Emissão", "Emitente", "CNPJ Emit", "Destinatário", "CNPJ Dest", "UF Emit", "UF Dest", "Nat.Op", "CFOP", "Valor Oficial", "Valor Itens", "vICMS", "vST", "vIPI", "vPIS", "vCOFINS", "Status"];
        const rows = notasFiltradas.map((n) => [
          n.chave, n.modelo, n.numero, n.serie,
          n.emissao?.slice(0, 10) ?? "",
          n.emitente, n.cnpjEmit, n.destinatario, n.cnpjDest,
          n.ufEmit, n.ufDest, n.natOp, n.cfopPrincipal,
          n.valorOficial.toFixed(2), n.valorItens.toFixed(2),
          n.vICMS.toFixed(2), n.vST.toFixed(2), n.vIPI.toFixed(2), n.vPIS.toFixed(2), n.vCOFINS.toFixed(2),
          n.notaStatus,
        ]);
        const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
        const bom = "\uFEFF";
        const blob = new Blob([bom + csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `notas_fiscais_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success("Excel (CSV) exportado com sucesso.");
        addLog({ id: crypto.randomUUID(), tipo: "Excel", timestamp: new Date().toISOString(), totalNotas: notasFiltradas.length, status: "success", label: `${fmtNum(notasFiltradas.length)} notas em CSV` });
      } catch (e: any) {
        toast.error("Falha ao gerar Excel: " + (e?.message ?? "erro"));
        addLog({ id: crypto.randomUUID(), tipo: "Excel", timestamp: new Date().toISOString(), totalNotas: 0, status: "error", label: "Erro na exportação" });
      } finally {
        setExportando(null);
      }
    }, 50);
  };

  

  return (
    <div className="px-4 lg:px-8 py-4 lg:py-8 max-w-[1600px] mx-auto space-y-6 lg:space-y-10 animate-in fade-in duration-500">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl lg:text-3xl font-extrabold tracking-tight dark:text-white text-slate-900">Exportações</h1>
          <p className="text-xs lg:text-sm text-muted-foreground mt-1">
            Gere relatórios detalhados e planilhas de dados fiscais processados.
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 lg:px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl w-fit">
          <Download className="size-4 text-blue-600 dark:text-blue-400" />
          <span className="text-xs lg:text-sm font-medium text-blue-900 dark:text-blue-200">Disponível: {fmtNum(notas.length)} documentos</span>
        </div>
      </header>

      {/* Painel de Filtros Glass */}
      <div className="glass-card p-6 lg:p-8 space-y-6 border-blue-500/10 animate-in-card" style={{ animationDelay: '100ms' }}>
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="space-y-4">
            <h2 className="text-xs font-black uppercase tracking-[0.2em] text-blue-500 flex items-center gap-2">
              <Filter className="size-4" /> Critérios de Exportação
            </h2>
            <div className="flex flex-wrap gap-6">
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Status da Nota</label>
                <div className="flex rounded-xl border border-slate-200 dark:border-white/10 bg-white/5 glass p-0.5">
                  {(["all", "AUTORIZADA", "CONTINGENCIA", "CANCELADA", "INUTILIZADA"] as const).map((s) => (
                    <button
                      key={s}
                      onClick={() => setFiltroStatusLocal(s)}
                      className={`px-3 py-1.5 text-[10px] font-bold rounded-lg transition-colors ${filtroStatus === s ? "bg-primary text-white shadow-sm" : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10"}`}
                    >
                      {s === "all" ? "Todos" : s === "CONTINGENCIA" ? "Contingência" : s === "INUTILIZADA" ? "Inutilizada" : s === "CANCELADA" ? "Cancelada" : "Autorizada"}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Modelo do Documento</label>
                <div className="flex rounded-xl border border-slate-200 dark:border-white/10 bg-white/5 glass p-0.5">
                  {(["all", "55", "65"] as const).map((m) => (
                    <button
                      key={m}
                      onClick={() => setFiltroModelo(m)}
                      className={`px-3 py-1.5 text-[10px] font-bold rounded-lg transition-colors ${filtroModelo === m ? "bg-primary text-white shadow-sm" : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10"}`}
                    >
                      {m === "all" ? "Todos" : m === "55" ? "NF-e (55)" : "NFC-e (65)"}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          <div className="lg:text-right space-y-1 bg-blue-500/5 p-4 rounded-2xl border border-blue-500/10 min-w-[240px]">
            <div className="text-[10px] font-black text-blue-500 uppercase tracking-[0.2em]">Resumo da Seleção</div>
            <div className="text-2xl font-black text-slate-900 dark:text-white tabular-nums">{fmtNum(notasFiltradas.length)} <span className="text-xs font-bold text-muted-foreground uppercase ml-1">Notas</span></div>
            <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">{fmtBRL(notasFiltradas.reduce((s, n) => s + n.valorOficial, 0))}</div>
          </div>
        </div>
      </div>

      {/* Botões de exportação Modernos */}
      <div className="grid md:grid-cols-2 gap-8">
        <div className="glass-card p-8 flex flex-col gap-6 relative overflow-hidden group border-red-500/10 animate-in-card hover-lift" style={{ animationDelay: '200ms' }}>
          <div className="absolute -right-8 -top-8 size-40 bg-red-500/10 blur-3xl rounded-full group-hover:bg-red-500/20 transition-all duration-700"></div>
          <div className="flex items-center gap-5 relative z-10">
            <div className="size-16 rounded-2xl bg-red-500/20 border border-red-500/30 flex items-center justify-center shadow-[0_0_30px_rgba(239,68,68,0.2)]">
              <FileText className="size-8 text-red-500" />
            </div>
            <div>
              <h3 className="text-lg font-black tracking-tight text-slate-900 dark:text-white">Exportar Relatório PDF</h3>
              <p className="text-[11px] font-bold text-red-500/60 uppercase tracking-widest mt-0.5">Dashboard + Documentos</p>
            </div>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed relative z-10">
            Gera um documento técnico formatado contendo os KPIs do dashboard, informações tributárias consolidadas e a listagem completa das notas filtradas. Ideal para conferências e apresentações internas.
          </p>
          <Button 
            onClick={handlePDF} 
            disabled={exportando !== null || notasFiltradas.length === 0}
            className="h-12 rounded-xl gap-3 shadow-xl shadow-red-500/30 bg-red-600 hover:bg-red-500 text-white border-none font-bold text-sm transition-all transform hover:scale-[1.02] active:scale-[0.98] relative z-10"
          >
            {exportando === "pdf" ? <Loader2 className="size-5 animate-spin" /> : <Download className="size-5" />}
            {exportando === "pdf" ? "Gerando Documento..." : `Gerar PDF (${fmtNum(notasFiltradas.length)} notas)`}
          </Button>
        </div>

        <div className="glass-card p-8 flex flex-col gap-6 relative overflow-hidden group border-emerald-500/10 animate-in-card hover-lift" style={{ animationDelay: '300ms' }}>
          <div className="absolute -right-8 -top-8 size-40 bg-emerald-500/10 blur-3xl rounded-full group-hover:bg-emerald-500/20 transition-all duration-700"></div>
          <div className="flex items-center gap-5 relative z-10">
            <div className="size-16 rounded-2xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center shadow-[0_0_30px_rgba(16,185,129,0.2)]">
              <Table2 className="size-8 text-emerald-500" />
            </div>
            <div>
              <h3 className="text-lg font-black tracking-tight text-slate-900 dark:text-white">Exportar Planilha CSV</h3>
              <p className="text-[11px] font-bold text-emerald-500/60 uppercase tracking-widest mt-0.5">Dados Tabulares Completos</p>
            </div>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed relative z-10">
            Exporta todos os campos técnicos das notas selecionadas em formato CSV compatível com Excel e Google Sheets. Inclui detalhamento de impostos (PIS, COFINS, ICMS) por documento.
          </p>
          <Button 
            onClick={handleExcel} 
            disabled={exportando !== null || notasFiltradas.length === 0}
            className="h-12 rounded-xl gap-3 shadow-xl shadow-emerald-500/30 bg-emerald-600 hover:bg-emerald-500 text-white border-none font-bold text-sm transition-all transform hover:scale-[1.02] active:scale-[0.98] relative z-10"
          >
            {exportando === "excel" ? <Loader2 className="size-5 animate-spin" /> : <Download className="size-5" />}
            {exportando === "excel" ? "Processando Dados..." : `Gerar CSV (${fmtNum(notasFiltradas.length)} notas)`}
          </Button>
        </div>
      </div>

      {/* Log de exportações Moderno */}
      {logs.length > 0 && (
        <div className="glass-card p-6 lg:p-8 space-y-6 border-slate-200/10 animate-in-card" style={{ animationDelay: '400ms' }}>
          <h2 className="text-xs font-black uppercase tracking-[0.25em] text-slate-500 flex items-center gap-3">
            <Clock className="size-4 text-blue-500" /> Histórico de Exportações
          </h2>
          <div className="space-y-3">
            {logs.map((log) => (
              <div key={log.id} className="flex items-center gap-4 p-4 rounded-xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.05] transition-all group">
                <div className={`size-10 rounded-xl flex items-center justify-center ${log.status === "success" ? "bg-emerald-500/10 text-emerald-500" : "bg-red-500/10 text-red-500"}`}>
                  {log.status === "success" ? <CheckCircle2 className="size-5" /> : <Download className="size-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-tighter ${log.tipo === "PDF" ? "bg-red-500/20 text-red-400" : "bg-emerald-500/20 text-emerald-400"}`}>
                      {log.tipo}
                    </span>
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-300 truncate">{log.label}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-black text-slate-400 tabular-nums uppercase tracking-widest">{fmtDate(log.timestamp)}</div>
                  <div className="text-[9px] font-bold text-slate-500 tabular-nums">{new Date(log.timestamp).toLocaleTimeString("pt-BR", { hour: '2-digit', minute: '2-digit' })}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
